import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Building, Pencil, X, ArrowLeft, 
  MapPin, User, Camera, Tag, Home,
  IndianRupee, Users, FileText, Briefcase, Phone,
  ChevronDown, ChevronUp, Store, MapPinned
} from 'lucide-react';

import api from '../apiConfig/api';

// Existing components remain the same
const ImageThumbnail = React.memo(({ image, index, onClick }) => {
  // Component implementation remains unchanged
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const imageUrl = useMemo(() => {
    if (!image?.path) return null;
    return `https://ras-tndma.in/uploads/survey/${image.path}`;
  }, [image]);

  if (!imageUrl) return null;

  return (
    <div 
      className="aspect-square rounded-lg overflow-hidden border border-gray-200 cursor-pointer relative bg-gray-50"
      onClick={onClick}
    >
      {isLoading && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <img
        src={imageUrl}
        alt={`Property ${index + 1}`}
        className={`w-full h-full object-cover transition-all duration-300 ${
          isLoading || hasError ? 'opacity-0' : 'opacity-100 hover:scale-105'
        }`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setHasError(true);
        }}
      />
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <Camera className="h-8 w-8 text-gray-400" />
        </div>
      )}
    </div>
  );
});

const ImageGallery = React.memo(({ images }) => {
  // Component implementation remains unchanged
  const [selectedImage, setSelectedImage] = useState(null);
  
  const imageArray = useMemo(() => {
    try {
      // Handle string input
      const parsed = typeof images === 'string' ? JSON.parse(images) : images;
      
      // Handle array of objects with path property
      if (Array.isArray(parsed)) {
        return parsed.map(item => {
          if (typeof item === 'string') {
            return { path: item, type: 'property' }; // Default type for backward compatibility
          }
          return item;
        });
      }
      
      // Handle array-like object
      if (parsed && typeof parsed === 'object') {
        return Object.values(parsed).map(item => {
          if (typeof item === 'string') {
            return { path: item, type: 'property' }; // Default type for backward compatibility
          }
          return item;
        });
      }
      
      return [];
    } catch (error) {
      console.error('Failed to parse images:', error);
      return [];
    }
  }, [images]);

  // Group images by type
  const groupedImages = useMemo(() => {
    if (!imageArray?.length) return { selfie: [], property: [] };
    
    return imageArray.reduce((acc, image) => {
      if (image.type === 'selfie') {
        acc.selfie.push(image);
      } else {
        acc.property.push(image);
      }
      return acc;
    }, { selfie: [], property: [] });
  }, [imageArray]);

  if (!imageArray?.length) return null;

  return (
    <div className="space-y-6">
      {/* Selfie Section */}
      {groupedImages.selfie.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <User className="h-4 w-4 text-blue-600" />
            Surveyor Selfie
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {groupedImages.selfie.map((image, index) => (
              <ImageThumbnail
                key={`selfie-${image.path}-${index}`}
                image={image}
                index={index}
                onClick={() => setSelectedImage(image)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Property Photos Section */}
      {groupedImages.property.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Building className="h-4 w-4 text-blue-600" />
            Property Photos
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {groupedImages.property.map((image, index) => (
              <ImageThumbnail
                key={`property-${image.path}-${index}`}
                image={image}
                index={index}
                onClick={() => setSelectedImage(image)}
              />
            ))}
          </div>
        </div>
      )}

      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-md w-full">
            <img
              src={`https://ras-tndma.in/uploads/survey/${selectedImage.path}`}
              alt={selectedImage.type === 'selfie' ? "Surveyor Selfie" : "Property Photo"}
              className="w-full h-auto object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(null);
              }}
              className="absolute top-2 right-2 p-2 bg-white rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <span className="px-3 py-1 bg-black/70 text-white text-sm rounded-full">
                {selectedImage.type === 'selfie' ? "Surveyor Selfie" : "Property Photo"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

const InfoCard = ({ icon: Icon, title, children, className }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 ${className || ''}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className="bg-blue-50 p-2 rounded-lg">
        <Icon className="h-5 w-5 text-blue-600" />
      </div>
      <h3 className="font-semibold text-gray-900">{title}</h3>
    </div>
    {children}
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="py-2">
    <div className="text-sm text-gray-500">{label}</div>
    <div className="font-medium text-gray-900">{value || 'N/A'}</div>
  </div>
);

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

// Helper function to format firm type for display
const formatFirmType = (firmType) => {
  if (!firmType) return 'N/A';
  return firmType.charAt(0).toUpperCase() + firmType.slice(1);
};

// Collapsible entity detail component to save space
const EntityDetailAccordion = ({ entity, index, totalEntities, defaultExpanded }) => {
  // Component implementation with added firm type display
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || false);
  
  // Check if this is a residential entity
  const isResidential = entity.firm_type === 'residential';
  
  // Determine entity type
  const isIndividual = !entity.has_prof_tax && !entity.has_trade_license && !isResidential;
  const entityType = 
    isResidential ? "residential" :
    entity.has_prof_tax ? "profTax" : 
    entity.has_trade_license ? "tradeLicense" : 
    "individual";

  // Get appropriate icon and colors
  const getEntityIcon = () => {
    switch(entityType) {
      case "residential": return <Home className="h-4 w-4 text-blue-600" />;
      case "profTax": return <FileText className="h-4 w-4 text-green-600" />;
      case "tradeLicense": return <Briefcase className="h-4 w-4 text-amber-600" />;
      case "individual": 
      default: return <User className="h-4 w-4 text-gray-600" />;
    }
  };
  
  const getBadgeStyle = () => {
    switch(entityType) {
      case "residential":
        return { bg: "#DBEAFE", text: "#1E40AF" };
      case "profTax":
        return { bg: "#DCFCE7", text: "#166534" };
      case "tradeLicense":
        return { bg: "#FEF3C7", text: "#92400E" };
      case "individual":
      default:
        return { bg: "#F3F4F6", text: "#4B5563" };
    }
  };

  const badge = getBadgeStyle();
  
  // Get entity title
  const getEntityTitle = () => {
    if (isResidential) return "Residential Space";
    if (entity.firm_name) return entity.firm_name;
    
    const typeLabel = 
      entityType === "profTax" ? "Prof. Tax" : 
      entityType === "tradeLicense" ? "Trade License" : 
      "Individual";
    
    return `${typeLabel} ${index + 1}`;
  };

  // Format entity summary (shown even when collapsed)
  const getEntitySummary = () => {
    const parts = [];
    
    // Always add firm type to the summary if available
    if (entity.firm_type) {
      parts.push(`Type: ${formatFirmType(entity.firm_type)}`);
    }
    
    // For residential entities, only show the area
    if (isResidential) {
      if (entity.commercial_area) {
        parts.push(`${entity.commercial_area} sq.ft`);
      }
      return parts.join(' • ');
    }
    
    // For other entities, show all relevant details
    if (entity.has_prof_tax && entity.prof_tax_no) {
      parts.push(`PT: ${entity.prof_tax_no}`);
    }
    
    if (entity.has_trade_license && entity.trade_license_no) {
      parts.push(`TL: ${entity.trade_license_no}`);
    }
    
    // Add GST information to summary
    if (entity.gst_number) {
      parts.push(`GST: ${entity.gst_number}`);
    }
    
    if (entity.total_employees) {
      parts.push(`${entity.total_employees} employees`);
    }
    
    if (entity.total_tax) {
      parts.push(formatCurrency(entity.total_tax));
    }
    
    return parts.join(' • ');
  };

  return (
    <div className={`border rounded-lg overflow-hidden mb-2 ${
      entityType === "residential" ? "border-blue-200" :
      entityType === "profTax" ? "border-green-200" : 
      entityType === "tradeLicense" ? "border-amber-200" : 
      "border-gray-200 bg-gray-50"
    }`}>
      {/* Header - always visible */}
      <div 
        className={`flex items-center justify-between p-3 cursor-pointer ${
          entityType === "residential" ? "bg-blue-50" :
          entityType === "profTax" ? "bg-green-50" : 
          entityType === "tradeLicense" ? "bg-amber-50" : 
          "bg-gray-100"
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {getEntityIcon()}
          <span className="font-medium text-gray-800">{getEntityTitle()}</span>
          <span 
            className="text-xs px-2 py-0.5 rounded-full" 
            style={{ backgroundColor: badge.bg, color: badge.text }}
          >
            {entityType === "residential" ? "Residential" :
             entityType === "profTax" ? "Professional Tax" : 
             entityType === "tradeLicense" ? "Trade License" : 
             "Individual"}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500 hidden sm:inline-block">
            {getEntitySummary()}
          </span>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>
      
      {/* Details - only visible when expanded */}
      {isExpanded && (
        <div className="p-3">
          {/* Display firm type for all entities */}
          {entity.firm_type && (
            <DetailRow label="Firm Type" value={formatFirmType(entity.firm_type)} />
          )}
          
          {/* For residential entities, just show the area */}
          {isResidential ? (
            <DetailRow 
              label="Used Area" 
              value={`${entity.commercial_area || 0} sq.ft`} 
            />
          ) : (
            <>
              {/* Entity contact details */}
              {(entity.owner_name || entity.mobile_number || entity.firm_name) && (
                <div className="mb-4 p-3 bg-white rounded-lg border border-gray-100">
                  {entity.firm_name && <DetailRow label="Firm Name" value={entity.firm_name} />}
                  {entity.owner_name && <DetailRow label="Owner Name" value={entity.owner_name} />}
                  {entity.mobile_number && <DetailRow label="Contact Number" value={entity.mobile_number} />}
                </div>
              )}
              
              {/* Area */}
              {entity.commercial_area && (
                <DetailRow label="Used Area" value={`${entity.commercial_area} sq.ft`} />
              )}
              
              {/* Registration info */}
              <div className="mb-3">
                {/* GST Number information */}
                {entity.gst_number && (
                  <DetailRow label="GST Number" value={entity.gst_number} />
                )}
                
                {entity.has_prof_tax && (
                  <DetailRow label="Professional Tax Number" value={entity.prof_tax_no} />
                )}
                
                {entity.has_trade_license && (
                  <DetailRow label="Trade License Number" value={entity.trade_license_no} />
                )}
                
                {isIndividual && !entity.gst_number && (
                  <div className="py-2 text-sm text-gray-500 italic">
                    No GST, professional tax or trade license registration
                  </div>
                )}
              </div>
              
              {/* Employee and Tax Details */}
              <DetailRow label="Total Employees" value={entity.total_employees} />
              <DetailRow label="Total Tax Amount" value={formatCurrency(entity.total_tax)} />
              
              {/* Employee Salary Distribution */}
              {entity.emp_details && Object.keys(entity.emp_details).length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <div className="text-sm font-medium text-gray-700">Employee Distribution</div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse bg-white rounded-lg">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left p-2 text-xs font-medium text-gray-500">Salary Range</th>
                          <th className="text-center p-2 text-xs font-medium text-gray-500">Count</th>
                          <th className="text-right p-2 text-xs font-medium text-gray-500">Tax Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(entity.emp_details).map((key) => {
                          const range = entity.emp_details[key];
                          return (
                            <tr key={key} className="border-t border-gray-200">
                              <td className="p-2 text-sm text-gray-600">
                                {range.range || 'Base salary'}
                              </td>
                              <td className="p-2 text-sm text-gray-600 text-center">
                                {range.count}
                              </td>
                              <td className="p-2 text-sm text-gray-600 text-right">
                                {formatCurrency(range.tax)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// New component to handle business details
const BusinessInfoCard = ({ structureDetails }) => {
  // Parse structure details if it's a string
  const businessDetails = useMemo(() => {
    try {
      if (typeof structureDetails === 'string') {
        return JSON.parse(structureDetails);
      }
      return structureDetails;
    } catch (error) {
      console.error('Error parsing structure details:', error);
      return null;
    }
  }, [structureDetails]);

  // If there's no business, return null
  if (!businessDetails?.has_business) {
    return null;
  }

  const location = businessDetails.business_location || {};
  const isWithinULB = location.is_within_ulb === true;

  return (
    <InfoCard icon={Store} title="Business Details" className="bg-white">
      {isWithinULB ? (
        // For business within ULB
        <>
          {location.business_name && (
            <DetailRow label="Business Name" value={location.business_name} />
          )}
          {location.business_type && (
            <DetailRow label="Business Type" value={location.business_type} />
          )}
          {location.ward_no && (
            <DetailRow label="Ward Number" value={location.ward_no} />
          )}
          <div className="mb-2 mt-3">
            <div className="flex items-center gap-2 mb-1">
              <MapPinned className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Business Location</span>
            </div>
            <div className="px-3 py-2 bg-blue-50 text-gray-800 rounded-lg mt-1">
              <div className="text-sm">
                <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium mr-2">
                  Within ULB
                </span>
                {location.address}
              </div>
            </div>
          </div>
          {location.gst_number && (
            <DetailRow label="GST Number" value={location.gst_number} />
          )}
        </>
      ) : (
        // For business outside ULB
        <>
          {location.location_type && (
            <DetailRow 
              label="Location Type" 
              value={location.location_type.charAt(0).toUpperCase() + location.location_type.slice(1)} 
            />
          )}
          <div className="mb-2 mt-3">
            <div className="flex items-center gap-2 mb-1">
              <MapPinned className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-gray-700">Business Location</span>
            </div>
            <div className="px-3 py-2 bg-amber-50 text-gray-800 rounded-lg mt-1">
              <div className="text-sm">
                <span className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium mr-2">
                  Outside ULB
                </span>
                {location.address}
              </div>
            </div>
          </div>
          {location.gst_number && (
            <DetailRow label="GST Number" value={location.gst_number} />
          )}
        </>
      )}
    </InfoCard>
  );
};

const AssetReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [assetData, setAssetData] = useState(null);
  const [expandAll, setExpandAll] = useState(false);

  useEffect(() => {
    const fetchAssetDetails = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`/asset/report/${id}`);
        setAssetData(response.data.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssetDetails();
  }, [id]);

  const ownerDetails = useMemo(() => {
    try {
      return assetData?.owner_det ? JSON.parse(assetData.owner_det) : null;
    } catch (error) {
      console.error('Error parsing owner details:', error);
      return null;
    }
  }, [assetData?.owner_det]);

  const profTaxDetails = useMemo(() => {
    // Check if prof_tax is already an object or a string that needs parsing
    if (assetData?.prof_tax) {
      if (typeof assetData.prof_tax === 'object') {
        return assetData.prof_tax;
      } else {
        try {
          return JSON.parse(assetData.prof_tax);
        } catch (error) {
          console.error('Error parsing prof tax details:', error);
          return null;
        }
      }
    }
    return null;
  }, [assetData?.prof_tax]);

  // Parse created_by information properly
  const createdByInfo = useMemo(() => {
    if (!assetData?.created_by) return null;
    
    try {
      // If it's an object, it may have already been parsed by the API
      if (typeof assetData.created_by === 'object' && !Array.isArray(assetData.created_by)) {
        return assetData.created_by;
      }
      
      // If it's a string, try to parse it
      if (typeof assetData.created_by === 'string') {
        return JSON.parse(assetData.created_by);
      }
      
      // If it's a number (user ID), check if we have user info in the response
      if (typeof assetData.created_by === 'number' && assetData.user) {
        return {
          id: assetData.created_by,
          name: assetData.user.Name,
          mobile: assetData.user.MobileNo,
          role: assetData.user.DesignationName
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing created_by info:', error);
      return null;
    }
  }, [assetData?.created_by, assetData?.user]);

  // Count different types of entities
  const entityCounts = useMemo(() => {
    if (!profTaxDetails?.entities) return { 
      total: 0, profTax: 0, tradeLicense: 0, individual: 0, residential: 0
    };
    
    // First count the total number of entities
    const total = profTaxDetails.entities.length;
    
    // Then count each type separately
    return profTaxDetails.entities.reduce((acc, entity) => {
      // Set total to the actual count of entities
      acc.total = total;
      
      // Count entity types (these are mutually exclusive)
      if (entity.firm_type === 'residential') {
        acc.residential += 1;
      } else if (entity.has_prof_tax) {
        acc.profTax += 1;
      } else if (entity.has_trade_license) {
        acc.tradeLicense += 1;
      } else {
        acc.individual += 1;
      }
      
      return acc;
    }, { total, profTax: 0, tradeLicense: 0, individual: 0, residential: 0 });
  }, [profTaxDetails?.entities]);

  // Group entities by type while maintaining a logical order
  const sortedEntities = useMemo(() => {
    if (!profTaxDetails?.entities?.length) return [];
    
    // Create a copy to avoid modifying the original
    return [...profTaxDetails.entities].sort((a, b) => {
      // Priority: 1. Professional Tax, 2. Trade License, 3. Residential, 4. Individual
      if (a.has_prof_tax && !b.has_prof_tax) return -1;
      if (!a.has_prof_tax && b.has_prof_tax) return 1;
      
      if (a.has_trade_license && !b.has_trade_license) return -1;
      if (!a.has_trade_license && b.has_trade_license) return 1;
      
      // Residential entities after tax entities but before individual entities
      if (a.firm_type === 'residential' && b.firm_type !== 'residential') {
        if (!b.has_prof_tax && !b.has_trade_license) return -1;
        return 1;
      }
      if (a.firm_type !== 'residential' && b.firm_type === 'residential') {
        if (!a.has_prof_tax && !a.has_trade_license) return 1;
        return -1;
      }
      
      return 0;
    });
  }, [profTaxDetails?.entities]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 mt-16">
      <div className="max-w-7xl mx-auto px-4">
  <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex flex-col">
                <div className="flex items-center gap-3">
                  <Tag className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-gray-600">#{assetData?.asst_ref}</span>
                </div>
                {createdByInfo && (
                  <div className="text-xs text-gray-500 mt-1">
                    Created by: {createdByInfo.name || 'Unknown'}, 
                    {createdByInfo.mobile ? ` ${createdByInfo.mobile},` : ''} 
                    {createdByInfo.role || ''}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="py-4 grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          <InfoCard icon={Building} title="Organization Details">
            <DetailRow label="Organization Name" value={assetData?.org_name} />
            <DetailRow label="Zone" value={assetData?.zone_name} />
            <DetailRow label="Ward" value={assetData?.ward_name} />
          </InfoCard>

          <InfoCard icon={User} title="Owner Details">
            <DetailRow label="Owner Name" value={assetData?.owner} />
            <DetailRow label="Mobile Number" value={ownerDetails?.mobile} />
          </InfoCard>

          <InfoCard icon={Home} title="Property Details">
            <DetailRow label="Usage Type" value={assetData?.usage} />
            <DetailRow label="Builtup Area (as per Record)" value={`${assetData?.buildarea || 'N/A'} sq.ft`} />
            <DetailRow label="Builtup Area (as Observed)" value={`${assetData?.area || 'N/A'} sq.ft`} />
          </InfoCard>

          {/* New Business Details Card - Will only render if has_business is true */}
          {assetData?.str_det && (
            <BusinessInfoCard structureDetails={assetData.str_det} />
          )}

       {/* Tax and Entity Section */}
       {profTaxDetails?.entities && profTaxDetails.entities.length > 0 && (
            <div className="xl:col-span-3">
              <InfoCard icon={IndianRupee} title="Tax & Entity Details">
                <div className="mb-4">
                  <DetailRow 
                    label="Total Tax Amount" 
                    value={formatCurrency(profTaxDetails.total_tax)} 
                  />
                  
                  {/* Entity Breakdown Summary */}
                  {entityCounts.total > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex flex-wrap items-center justify-between">
                        <div className="flex flex-wrap gap-2 mb-2">
                          <div className="px-3 py-1 bg-blue-50 rounded-lg text-sm">
                            <span className="font-medium">{entityCounts.total}</span> Total
                          </div>
                          {entityCounts.profTax > 0 && (
                            <div className="px-3 py-1 bg-green-50 rounded-lg text-sm">
                              <span className="font-medium">{entityCounts.profTax}</span> Prof. Tax
                            </div>
                          )}
                          {entityCounts.tradeLicense > 0 && (
                            <div className="px-3 py-1 bg-amber-50 rounded-lg text-sm">
                              <span className="font-medium">{entityCounts.tradeLicense}</span> Trade License
                            </div>
                          )}
                          {entityCounts.residential > 0 && (
                            <div className="px-3 py-1 bg-blue-100 rounded-lg text-sm">
                              <span className="font-medium">{entityCounts.residential}</span> Residential
                            </div>
                          )}
                          {entityCounts.individual > 0 && (
                            <div className="px-3 py-1 bg-gray-100 rounded-lg text-sm">
                              <span className="font-medium">{entityCounts.individual}</span> Individual
                            </div>
                          )}
                        </div>
                        {/* Toggle expand all button */}

                      </div>
                    </div>
                  )}
                </div>
                
                {/* Entity Accordions */}
                <div>
                  {sortedEntities.map((entity, index) => (
                    <EntityDetailAccordion
                      key={`entity-${index}`}
                      entity={entity}
                      index={index}
                      totalEntities={sortedEntities.length}
                      defaultExpanded={expandAll || index === 0} // Expand first one by default
                    />
                  ))}
                </div>
              </InfoCard>
            </div>
          )}

          {assetData?.images && (
            <div className="xl:col-span-3">
              <InfoCard icon={Camera} title="Property Images">
                <div className="mt-3">
                  <ImageGallery images={assetData.images} />
                </div>
              </InfoCard>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AssetReport;